/**
 * Movable Ranges — derives draggable Magic-block ranges from `magicScanField`.
 *
 * A "movable range" is a contiguous set of lines that can be moved as a unit
 * via the drag-handle gutter. We cover all six top-level Magic block kinds
 * plus block-form `::for` (which does not appear in `MagicBlockScan`; we read
 * it from `analysis.foldBlocks` keyed `for:<id>`).
 *
 * Single-line forms (e.g. inline `::import{from="..." ns="x"}` with no body)
 * are skipped — we only treat block-form imports as movable.
 *
 * Parent/child containment is resolved with a stack walk over ranges sorted
 * by `fromLine`. Drop targets for a given source range are the line
 * boundaries between sibling ranges (filtered to exclude positions inside
 * the source's own subtree).
 */

import type { EditorState } from "@codemirror/state";
import { magicScanField } from "./scan-state";

export type MovableKind =
    | "define"
    | "for"
    | "content"
    | "meta"
    | "import"
    | "use"
    | "render";

export interface MovableRange {
    /** Stable ID, e.g. "define:foo" / "for:loop1" / "meta:publish" */
    id: string;
    kind: MovableKind;
    /** Human label used in drag preview / a11y */
    label: string;
    /** 1-based inclusive line numbers */
    fromLine: number;
    toLine: number;
    /** Document positions: fromPos = doc.line(fromLine).from, toPos = doc.line(toLine).to */
    fromPos: number;
    toPos: number;
    /** Immediate parent ID if nested inside another movable range */
    parentId?: string;
}

export interface DropTarget {
    /** Insertion offset (always at the start of a line, or doc end) */
    pos: number;
    /** 1-based line that this insertion sits before; doc.lines+1 means end-of-doc */
    beforeLine: number;
    /** Parent ID for sibling-group identity (undefined = top level) */
    parentId?: string;
}

/** Compute movable ranges from current editor state. */
export function getMovableRanges(state: EditorState): MovableRange[] {
    const analysis = state.field(magicScanField, false);
    if (!analysis) return [];

    const { scan, foldBlocks, renderBlocks } = analysis;
    const doc = state.doc;
    const ranges: MovableRange[] = [];

    const safeLine = (n: number) => Math.max(1, Math.min(n, doc.lines));

    for (const b of scan.defineBlocks) {
        const fromLine = safeLine(b.startLine);
        const toLine = safeLine(b.endLine);
        ranges.push({
            id: `define:${b.defId}`,
            kind: "define",
            label: `::define[${b.defId}]`,
            fromLine,
            toLine,
            fromPos: doc.line(fromLine).from,
            toPos: doc.line(toLine).to,
        });
    }

    for (const b of scan.metaBlocks) {
        const fromLine = safeLine(b.startLine);
        const toLine = safeLine(b.endLine);
        ranges.push({
            id: `meta:${b.domain}`,
            kind: "meta",
            label: `::meta[${b.domain}]`,
            fromLine,
            toLine,
            fromPos: doc.line(fromLine).from,
            toPos: doc.line(toLine).to,
        });
    }

    for (const b of scan.contentBlocks) {
        const fromLine = safeLine(b.startLine);
        const toLine = safeLine(b.endLine);
        ranges.push({
            id: `content:${b.defId}`,
            kind: "content",
            label: `::content[${b.defId}]`,
            fromLine,
            toLine,
            fromPos: doc.line(fromLine).from,
            toPos: doc.line(toLine).to,
        });
    }

    for (const b of scan.useBlocks) {
        const fromLine = safeLine(b.startLine);
        const toLine = safeLine(b.endLine);
        ranges.push({
            id: `use:${b.useId}`,
            kind: "use",
            label: `::use[${b.useId}]`,
            fromLine,
            toLine,
            fromPos: doc.line(fromLine).from,
            toPos: doc.line(toLine).to,
        });
    }

    // Standalone block-render lines (`::render[id]::` on its own line).
    // Same id may appear multiple times — disambiguate by line number.
    for (const r of renderBlocks) {
        const ln = safeLine(r.line);
        ranges.push({
            id: `render:${r.id}#${ln}`,
            kind: "render",
            label: `::render[${r.id}]`,
            fromLine: ln,
            toLine: ln,
            fromPos: doc.line(ln).from,
            toPos: doc.line(ln).to,
        });
    }

    // ::for blocks live only in foldBlocks under "for:<id>".
    // Block-form ::import{...} also has a foldBlocks entry under "import:<ns>".
    for (const [key, range] of foldBlocks) {
        if (key.startsWith("for:")) {
            const id = key.slice("for:".length);
            // Skip nested fold sub-keys like "for:x:something" — only base entries.
            if (id.includes(":")) continue;
            const fromLine = doc.lineAt(range.from).number; // header line (line ending == range.from)
            const toLine = doc.lineAt(range.to).number; // close line (line ending == range.to)
            ranges.push({
                id: `for:${id}`,
                kind: "for",
                label: `::for[${id}]`,
                fromLine: safeLine(fromLine),
                toLine: safeLine(toLine),
                fromPos: doc.line(safeLine(fromLine)).from,
                toPos: doc.line(safeLine(toLine)).to,
            });
        } else if (key.startsWith("import:")) {
            const ns = key.slice("import:".length);
            if (ns.includes(":")) continue; // skip sub-fence keys
            const fromLine = doc.lineAt(range.from).number;
            const toLine = doc.lineAt(range.to).number;
            ranges.push({
                id: `import:${ns}`,
                kind: "import",
                label: `::import ns="${ns}"`,
                fromLine: safeLine(fromLine),
                toLine: safeLine(toLine),
                fromPos: doc.line(safeLine(fromLine)).from,
                toPos: doc.line(safeLine(toLine)).to,
            });
        }
    }

    // Sort by fromLine ascending (tiebreak: longer range first, so parents
    // come before children when they share a start line — rare but safe).
    ranges.sort((a, b) => a.fromLine - b.fromLine || b.toLine - a.toLine);

    // Resolve parent containment via stack walk. A range R is the parent of
    // candidate C when R.fromLine < C.fromLine && C.toLine < R.toLine.
    const stack: MovableRange[] = [];
    for (const r of ranges) {
        while (stack.length > 0 && stack[stack.length - 1].toLine < r.toLine) {
            stack.pop();
        }
        // Top of stack now strictly contains r (or stack is empty → top level).
        // But we also require the parent to actually start before r.
        const top = stack.length > 0 ? stack[stack.length - 1] : undefined;
        if (
            top &&
            top.fromLine <= r.fromLine &&
            top.toLine >= r.toLine &&
            top !== r
        ) {
            r.parentId = top.id;
        }
        stack.push(r);
    }

    return ranges;
}

/** Returns true if `descendantId` is `ancestorId` or any of its descendants. */
export function isDescendantOf(
    ranges: MovableRange[],
    descendantId: string,
    ancestorId: string,
): boolean {
    if (descendantId === ancestorId) return true;
    const byId = new Map(ranges.map((r) => [r.id, r]));
    let cur = byId.get(descendantId);
    while (cur?.parentId) {
        if (cur.parentId === ancestorId) return true;
        cur = byId.get(cur.parentId);
    }
    return false;
}

/**
 * Compute legal drop targets when dragging `sourceId`.
 *
 * Strategy: enumerate all sibling boundaries inside the source's parent
 * group (or top-level if source has no parent). Skip positions that fall
 * inside the source's own range (no-op moves). Re-parenting is NOT supported
 * in MVP — sources stay within their parent container.
 */
export function getDropTargetsFor(
    state: EditorState,
    ranges: MovableRange[],
    sourceId: string,
): DropTarget[] {
    const source = ranges.find((r) => r.id === sourceId);
    if (!source) return [];

    const doc = state.doc;
    const parentId = source.parentId;
    const siblings = ranges.filter((r) => r.parentId === parentId);
    if (siblings.length === 0) return [];

    // Sort siblings by fromLine
    siblings.sort((a, b) => a.fromLine - b.fromLine);

    // Determine the container's inner line range:
    //   - top level: [1, doc.lines]
    //   - inside parent: parent.fromLine + 1 .. parent.toLine - 1
    let containerStart = 1;
    let containerEnd = doc.lines + 1; // exclusive (one past last line)
    if (parentId) {
        const parent = ranges.find((r) => r.id === parentId);
        if (parent) {
            containerStart = parent.fromLine + 1;
            containerEnd = parent.toLine; // before parent close line
        }
    }

    const targets: DropTarget[] = [];

    // Position before each sibling
    for (const s of siblings) {
        const beforeLine = s.fromLine;
        if (beforeLine < containerStart || beforeLine > containerEnd) continue;
        const pos = doc.line(beforeLine).from;
        // Skip positions strictly inside source's body (would land mid-range).
        // We allow `beforeLine === source.toLine + 1` (i.e. the boundary right
        // after the source) so the user still sees a drop indicator there;
        // the drag controller's commit-time no-op guard handles same-spot moves.
        if (beforeLine > source.fromLine && beforeLine <= source.toLine) continue;
        if (beforeLine === source.fromLine) continue;
        targets.push({ pos, beforeLine, parentId });
    }

    // Position after the last sibling (= end of container)
    const last = siblings[siblings.length - 1];
    if (last && last.id !== source.id) {
        const afterLine = last.toLine + 1;
        if (afterLine <= containerEnd) {
            const pos =
                afterLine > doc.lines
                    ? doc.length
                    : doc.line(afterLine).from;
            targets.push({ pos, beforeLine: afterLine, parentId });
        }
    }

    // Deduplicate by pos
    const seen = new Set<number>();
    return targets.filter((t) => {
        if (seen.has(t.pos)) return false;
        seen.add(t.pos);
        return true;
    });
}
